import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import {
  IPartCategoryMaterial,
  IPartCategoryMaterialRequest,
  IPartCategoryMaterialsResponse,
} from "../models/PartCategoryMaterial";

export default function usePartCategoryMaterials(categoryId: string | undefined) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const queryKey = ["categories", categoryId, "materials"];

  const partCategoryMaterials = useQuery({
    queryKey,
    queryFn: () => get<IPartCategoryMaterialsResponse>(`/material/categories/${categoryId}/links`),
    enabled: !!categoryId,
  });

  const invalidateQueries = () => {
    queryClient.invalidateQueries(queryKey);
    queryClient.invalidateQueries(["categories", "single", categoryId]);
    queryClient.invalidateQueries(["categories"]);
    queryClient.invalidateQueries(["categories", "partial"]);
  };

  const create = useMutation(
    (req: IPartCategoryMaterialRequest) => post(`/material/categories/${categoryId}/links`, req),
    {
      onSuccess: () => {
        invalidateQueries();
      },
    }
  );

  const update = useMutation(
    ({ linkId, req }: { linkId: string; req: IPartCategoryMaterialRequest }) =>
      put(`/material/categories/${categoryId}/links/${linkId}`, req),
    {
      onSuccess: () => {
        invalidateQueries();
      },
    }
  );

  const remove = useMutation(
    (linkId: string) => del(`/material/categories/${categoryId}/links/${linkId}`),
    {
      onSuccess: () => {
        invalidateQueries();
      },
    }
  );

  return {
    partCategoryMaterials,
    create,
    update,
    remove,
  };
}
